import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import CustomDialog from 'Components/CustomDialog'
import SPMActivityDialogButton from 'Components/SPMActivityDialogButton'
import { formatToUTCISO, isDateTodayOrInThePast } from 'Util/formatters'
import theme from 'Util/theme'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  TextField,
  Typography
} from '@mui/material'
import { useState } from 'react'
import { t } from 'ttag'

export interface ActivityData {
  active: boolean
  activity_end_date?: string | null
  additional_info?: string | null
}

interface SPMActivityDialogProps {
  open: boolean
  handleClose: () => void
  originalActivityEndDate: string | undefined
  isActive: boolean
  onSubmit: (e: React.FormEvent<HTMLFormElement>, activityData: ActivityData) => void
  isSubmitLoading: boolean
  spmObjectName: string
  originalAdditionalInfo?: string | null
}

export default function SPMActivityDialog({
  open,
  handleClose,
  originalActivityEndDate,
  isActive,
  onSubmit,
  isSubmitLoading,
  spmObjectName,
  originalAdditionalInfo
}: Readonly<SPMActivityDialogProps>) {
  const [activityEndDate, setActivityEndDate] = useState(originalActivityEndDate)
  const [additionalInfo, setAdditionalInfo] = useState(originalAdditionalInfo)
  const activityData: ActivityData = {
    active: true,
    activity_end_date: originalActivityEndDate,
    additional_info: originalAdditionalInfo
  }
  const isToBeCeased = originalActivityEndDate !== undefined

  const setActivityData = (): ActivityData => {
    activityData.additional_info = additionalInfo
    // If the SPM object is ceased or will be ceased, we activate it and wipe out the activity end date.
    if (!isActive || isToBeCeased) {
      activityData.activity_end_date = null
      activityData.active = true
    }
    // If the activityEndDate is today or in the past,
    // we instantly cease the SPM object (activity_end_date will be set by the SPM API).
    else if (activityEndDate && isDateTodayOrInThePast(new Date(activityEndDate))) {
      activityData.activity_end_date = null
      activityData.active = false
    }
    // If the activityEndDate is in the future, we set the activity_end_date of the SPM object and keep it active.
    else {
      activityData.activity_end_date = activityEndDate
      activityData.active = true
    }
    return activityData
  }

  const handleDateTimePickerChange = (value: Date | null) => {
    let selectedActivityEndDate = ''
    if (value instanceof Date) {
      selectedActivityEndDate = formatToUTCISO(value)
    }
    setActivityEndDate(selectedActivityEndDate)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAdditionalInfo(e.target.value)
  }

  const dialogCeaseContent = originalActivityEndDate ? (
    <Typography component="span" color="text.primary">
      {t`Are you sure you want to cancel ceasing the ${spmObjectName}?`}
    </Typography>
  ) : (
    <>
      <Box>
        <Typography component="span" color="text.primary">
          {t`Are you sure you want to cease the ${spmObjectName}? Enter activity end date below.`}
        </Typography>
      </Box>
      <FormControl sx={{ minWidth: 240, mr: 3, mt: 2 }} size="small">
        <CustomDateTimePicker
          onChange={handleDateTimePickerChange}
          name="activity_end_date"
          value={activityEndDate ? new Date(activityEndDate) : null}
          label={t`Activity end date`}
          isRequired={true}
        />
      </FormControl>
    </>
  )

  return (
    <CustomDialog open={open} handleClose={handleClose}>
      <DialogTitle>{t`Are you sure?`}</DialogTitle>
      <Box component="form" onSubmit={e => onSubmit(e, setActivityData())}>
        <DialogContent>
          <DialogContentText>
            {isActive ? (
              dialogCeaseContent
            ) : (
              <Typography
                component="span"
                color="text.primary"
              >{t`Are you sure you want to activate the ${spmObjectName}?`}</Typography>
            )}
            {/* Additional info can only be set for SPM objects that have the additional_info field in the API. 
            If the object has the additional_info field in the API, it is either null or string. 
            If the object doesn't have the additional info field in the API, 
            it will not be passed as a prop to this component, so it will be undefined. */}
            {originalAdditionalInfo !== undefined && (
              <Box mt={2}>
                <FormControl sx={{ minWidth: 400 }} size="small">
                  <TextField
                    name="additional_info"
                    value={additionalInfo}
                    label={t`Additional info`}
                    onChange={handleChange}
                    multiline={true}
                    rows={3}
                  />
                </FormControl>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button sx={{ color: theme.palette.text.primary }} onClick={handleClose}>
            {t`Cancel`}
          </Button>
          <SPMActivityDialogButton
            activityEndDate={originalActivityEndDate}
            isActive={isActive}
            isLoading={isSubmitLoading}
          />
        </DialogActions>
      </Box>
    </CustomDialog>
  )
}
